import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DateControls, defaultRange, type DateRange } from "../components/DateControls";
import { RankingTable, type RankingRow } from "../components/RankingTable";
import { TrendChart } from "../components/TrendChart";
import { useWorkspace } from "../contexts/WorkspaceContext";
import {
  errorMessage,
  formatDate,
  formatKes,
  formatNumber,
  formatQtyWithUnit,
  unwrap,
  validDateRange,
} from "../lib/format";
import type { ProductSalesRecord, ShopError, ShopReport } from "../lib/types";

type Section = "products" | "staff" | "customers" | "expenses" | "commissions";

export function OverviewPage() {
  const { actor, activeShop } = useWorkspace();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = useMemo(() => {
    const from = params.get("from") ?? "";
    const to = params.get("to") ?? "";
    return validDateRange(from, to) ? { from, to } : defaultRange();
  }, []); // Initial URL state only; subsequent changes are controlled locally.
  const [range, setRange] = useState<DateRange>(initial);
  const [report, setReport] = useState<ShopReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setParams({ from: range.from, to: range.to }, { replace: true });
  }, [range, setParams]);

  useEffect(() => {
    if (!actor) return;
    let live = true;
    setLoading(true);
    setError("");
    setReport(null);
    actor
      .getReport({ fromDate: range.from, toDate: range.to })
      .then((result) => {
        if (!live) return;
        if ("ok" in result) {
          setReport(result.ok);
          return;
        }
        const denied =
          "NotOwner" in result.err ||
          "NotAuthorized" in result.err ||
          "Anonymous" in result.err;
        if (denied) navigate("/not-authorized", { replace: true });
        setError(errorMessage(result.err));
      })
      .catch(() => {
        if (live) setError("Could not reach the shop canister. Check your connection and retry.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [actor, range.from, range.to, navigate]);

  function retry() {
    setRange({ ...range });
  }

  const products = unwrap(report?.products ?? []);
  const staff = unwrap(report?.staff ?? []);
  const customers = unwrap(report?.customers ?? []);
  const expenses = unwrap(report?.expenses ?? []);
  const commissions = unwrap(report?.commissions ?? []);
  const sections: { id: Section; label: string; visible: boolean }[] = [
    { id: "products", label: "Products", visible: !!products },
    { id: "staff", label: "Staff", visible: !!staff },
    { id: "customers", label: "Customers", visible: !!customers },
    { id: "expenses", label: "Expenses", visible: !!expenses },
    { id: "commissions", label: "Commissions", visible: !!commissions },
  ];

  return (
    <div className="page page-enter">
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Performance overview</p>
          <h1>{activeShop?.name}</h1>
        </div>
        <Link
          className="secondary-button"
          to={`/shops/${activeShop?.id.toText()}/daily?from=${range.from}&to=${range.to}`}
        >
          Daily detail <span aria-hidden="true">→</span>
        </Link>
      </div>

      <DateControls value={range} onChange={setRange} disabled={loading} />

      {loading ? <ReportSkeleton /> : null}
      {!loading && error ? (
        <div className="error-panel" role="alert">
          <strong>Report unavailable</strong>
          <p>{error}</p>
          <button className="text-button" onClick={retry}>Retry</button>
        </div>
      ) : null}
      {!loading && report ? (
        <>
          <section className="metric-strip" aria-label="Sales summary">
            <Metric label="Revenue" value={formatKes(report.sales.totalSalesKes)} primary />
            <Metric label="Transactions" value={formatNumber(report.sales.totalSales)} />
            <Metric label="Average sale" value={formatKes(report.sales.averageSaleKes)} />
            <Metric label="Discounts" value={formatKes(report.sales.totalDiscountKes)} />
            {unwrap(report.sales.marginKes) !== undefined ? (
              <Metric
                label="FIFO margin"
                value={formatKes(unwrap(report.sales.marginKes) ?? 0n)}
                note={`${formatNumber(unwrap(report.sales.marginPct) ?? 0n)}%`}
              />
            ) : null}
          </section>

          <section className="chart-section">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Daily revenue</p>
                <h2>Sales trend</h2>
              </div>
              <span>{formatDate(range.from, true)} — {formatDate(range.to, true)}</span>
            </div>
            <TrendChart
              days={report.sales.days}
              shopId={activeShop!.id.toText()}
              from={range.from}
              to={range.to}
            />
          </section>

          <section className="split-analysis">
            <div>
              <p className="eyebrow">Payment mix</p>
              <h2>Where revenue landed</h2>
              <PaymentRow label="M-Pesa" value={report.sales.totalMpesaKes} total={report.sales.totalSalesKes} />
              <PaymentRow label="Cash" value={report.sales.totalCashKes} total={report.sales.totalSalesKes} />
              <PaymentRow label="Card" value={report.sales.totalCardKes} total={report.sales.totalSalesKes} />
            </div>
            <div className="day-notes">
              <p className="eyebrow">Range markers</p>
              <DayLink label="Best day" day={unwrap(report.sales.bestDay)} shopId={activeShop!.id.toText()} range={range} />
              <DayLink label="Slowest day" day={unwrap(report.sales.slowestDay)} shopId={activeShop!.id.toText()} range={range} />
            </div>
          </section>

          <nav className="section-jumps" aria-label="Report sections">
            {sections.filter((section) => section.visible).map((section) => (
              <a key={section.id} href={`#${section.id}`}>{section.label}</a>
            ))}
          </nav>

          {products ? <ProductSection products={products} /> : null}
          {staff ? (
            <div id="staff" className="report-block">
              <BlockHeading eyebrow="Staff" title="Cashier performance" />
              <RankingTable title="By revenue" rows={staff.topByRevenue.map((row) => ({
                key: row.cashier.toText(),
                label: unwrap(row.cashierName) ?? shortPrincipal(row.cashier.toText()),
                value: formatKes(row.totalSalesKes),
                secondary: `${formatNumber(row.totalSales)} sales`,
              }))} />
            </div>
          ) : null}
          {customers ? (
            <div id="customers" className="report-block">
              <BlockHeading eyebrow="Customers" title="Customer value" />
              <section className="metric-strip compact">
                <Metric label="Known customers" value={formatNumber(customers.totalUniqueCustomers)} />
                <Metric label="Customer revenue" value={formatKes(customers.totalCustomerSalesKes)} />
                <Metric label="Average spend" value={formatKes(customers.averageSpendKes)} />
                <Metric label="Repeat customers" value={formatNumber(customers.repeatCustomers)} />
              </section>
              <RankingTable title="Top by revenue" rows={customers.topByRevenue.map((row) => ({
                key: row.id.toString(),
                label: unwrap(row.name) ?? `Customer #${row.id}`,
                value: formatKes(row.totalSalesKes),
                secondary: `${formatNumber(row.totalSales)} sales`,
              }))} />
            </div>
          ) : null}
          {expenses ? (
            <div id="expenses" className="report-block">
              <BlockHeading eyebrow="Expenses" title="Spending by category" />
              <section className="metric-strip compact">
                <Metric label="Total expenses" value={formatKes(expenses.totalExpensesKes)} />
                <Metric label="Entries" value={formatNumber(expenses.totalExpenses)} />
                <Metric label="Average expense" value={formatKes(expenses.averageExpenseKes)} />
              </section>
              <RankingTable title="Largest categories" rows={expenses.topByAmount.map((row) => ({
                key: `${row.id}:${unwrap(row.subTagName) ?? ""}`,
                label: expenseLabel(row),
                value: formatKes(row.totalExpensesKes),
                secondary: `${formatNumber(row.totalExpenses)} entries`,
              }))} />
            </div>
          ) : null}
          {commissions ? (
            <div id="commissions" className="report-block">
              <BlockHeading eyebrow="Commissions" title="Steward earnings" />
              <section className="metric-strip compact">
                <Metric label="Commission earned" value={formatKes(commissions.totalCommissionKes)} />
                <Metric
                  label="Highest earner"
                  value={unwrap(commissions.highestEarner)?.stewardName[0] ?? "—"}
                  note={unwrap(commissions.highestEarner) ? formatKes(unwrap(commissions.highestEarner)!.commissionKes) : undefined}
                />
              </section>
              <RankingTable title="Top earners" rows={commissions.topEarners.map((row) => ({
                key: row.stewardId.toString(),
                label: unwrap(row.stewardName) ?? `Steward #${row.stewardId}`,
                value: formatKes(row.commissionKes),
                secondary: `${formatNumber(row.totalSales)} sales`,
              }))} />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Metric({ label, value, note, primary = false }: { label: string; value: string; note?: string; primary?: boolean }) {
  return <div className={primary ? "metric primary" : "metric"}><span>{label}</span><strong>{value}</strong>{note ? <small>{note}</small> : null}</div>;
}

function PaymentRow({ label, value, total }: { label: string; value: bigint; total: bigint }) {
  const percent = total === 0n ? 0 : Math.round((Number(value) / Number(total)) * 100);
  return <div className="payment-row"><div><strong>{label}</strong><span>{formatKes(value)} · {percent}%</span></div><div className="meter"><span style={{ width: `${percent}%` }} /></div></div>;
}

function DayLink({ label, day, shopId, range }: { label: string; day?: ShopReport["sales"]["days"][number]; shopId: string; range: DateRange }) {
  if (!day) return <div className="day-link"><span>{label}</span><strong>—</strong></div>;
  return <Link className="day-link" to={`/shops/${shopId}/daily?from=${range.from}&to=${range.to}&day=${day.date}`}><span>{label}</span><strong>{formatDate(day.date, true)}</strong><small>{formatKes(day.totalSalesKes)}</small></Link>;
}

function BlockHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return <div className="block-heading"><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>;
}

function ProductSection({ products }: { products: NonNullable<ReturnType<typeof unwrap<ShopReport["products"][0]>>> }) {
  const [metric, setMetric] = useState<"revenue" | "items" | "sales" | "margin" | "marginKes">("revenue");
  const rowsByMetric: Record<typeof metric, ProductSalesRecord[]> = {
    revenue: products.topByRevenue,
    items: products.topByItems,
    sales: products.topBySales,
    margin: products.topByMargin,
    marginKes: products.topByMarginKes,
  };
  const rows: RankingRow[] = rowsByMetric[metric].map((row) => ({
    key: `${row.productId}:${unwrap(row.productVariantId)?.toString() ?? "parent"}`,
    label: productLabel(row),
    detail: unwrap(row.unit),
    value: metric === "items" ? formatQtyWithUnit(row.totalItems, row.unit) : metric === "sales" ? formatNumber(row.totalSales) : metric === "margin" ? `${formatNumber(unwrap(row.marginPct) ?? 0n)}%` : metric === "marginKes" ? formatKes(unwrap(row.marginKes) ?? 0n) : formatKes(row.totalSalesKes),
    secondary: metric === "revenue" ? formatQtyWithUnit(row.totalItems, row.unit) : undefined,
  }));
  return <div id="products" className="report-block"><BlockHeading eyebrow="Products" title="What moved the business" /><div className="metric-switch" role="group" aria-label="Product ranking metric">{([['revenue','Revenue'],['items','Items'],['sales','Transactions'],['margin','Margin %'],['marginKes','Margin KES']] as const).map(([id,label]) => <button key={id} className={metric === id ? "active" : ""} onClick={() => setMetric(id)}>{label}</button>)}</div><RankingTable title="Top products" rows={rows} /></div>;
}

function productLabel(row: ProductSalesRecord): string {
  const base = unwrap(row.name) ?? `Product #${row.productId}`;
  return unwrap(row.variantName) ? `${base} — ${unwrap(row.variantName)}` : base;
}

function expenseLabel(row: { id: bigint; name: [] | [string]; subTagName: [] | [string] }): string {
  const base = unwrap(row.name) ?? `Category #${row.id}`;
  return unwrap(row.subTagName) ? `${base} — ${unwrap(row.subTagName)}` : base;
}

function shortPrincipal(value: string): string { return value.length > 22 ? `${value.slice(0, 9)}…${value.slice(-7)}` : value; }

function ReportSkeleton() {
  return <div className="report-skeleton" aria-label="Loading report"><span /><span /><span /><div /></div>;
}
